import PayWithPaystack from './PayWithPaystack/PayWithPaystack'
import PayWithStripe from './PayWithStripe/PayWithStripe'
import PayWithFlutterwave from './PayWithFlutterwave/PayWithFlutterwave'
import * as React from 'react'
import { ISchool } from '../../interfaces/school.interface'
import { IUserDetails } from '../../interfaces/user.interface'
import { envConfig } from '../../services/envConfig'
import {
  Button,
  FormControl,
  ListItemText,
  TextField,
  List,
  ListItem,
  Paper,
  CircularProgress,
} from '@mui/material'
import DialogComponent from '../DialogComponent/DialogComponent'
import moment from 'moment'
import { ApplyCouponRequest } from '../../services/admin-service/api-request'
import { useSnackbarComponent } from '../SnackbarComponents/useSnackbarComponent'
import { AxiosPromise } from 'axios'
import ArrowDownIcon from '@mui/icons-material/ArrowDropDownOutlined'
import ArrowUpIcon from '@mui/icons-material/ArrowDropUpOutlined'

import {
  formatAmount,
  retrieveSchoolPaymentProvider,
} from '../../services/utils'
import HubspotSupportFormDialogButton from '../SchoolDetailsComponents/HubspotSupportFormDialogButton'
import { useIsSmallScreen } from '../utilsHooks/useIsSmallScreen'
import { AnalyticService } from '../../lib/analytics/analyticService'

const PAYSTACK_KEY = envConfig.NEXT_PUBLIC_PAYSTACK_KEY || ''

export default function PayWithPaymentProvider(props: {
  getCoupon: (code: string) => AxiosPromise
  school_details: ISchool
  userDetails: IUserDetails
  courseId?: string
  paymentPlanId?: string
  paymentPlanInvoiceId?: string
  amount: number
  paymentFor: 'school_fees' | 'add_card'
  productName: string
  onTransactionComplete?: () => void
  setTransactionComplete: (transactionComplete: boolean) => void
  buttonVariant?: 'contained' | 'outlined' | 'text'
  fullWidth?: boolean
  buttonSize?: 'large' | 'small' | 'medium'
  buttonStyle?: any
  openDialog: boolean
  setOpenDialog: (openDialog: boolean) => void
  courseTier?: number
}) {
  const [couponPrice, setCouponPrice] = React.useState(Number)
  const [couponCode, setCouponCode] = React.useState('')
  const [count, setCount] = React.useState(Number)
  const [error, setError] = React.useState('')
  const [discount, setDiscount] = React.useState(Number)
  const [showCoupon, setShowCoupon] = React.useState(false)
  const [applyCouponLoading, setApplyCouponLoading] = React.useState(false)
  const school_id = props.school_details._id
  const name = props.userDetails.name
  const user_id = props.userDetails._id
  const additional_info = props.userDetails.user_description
  const email = props.userDetails.email
  const phone_number = props.userDetails.phone_number
  const payment_for = props.paymentFor
  const course_id = props.courseId
  const payments_alert_id = props.paymentPlanId
  const payment_plan_invoice_id = props.paymentPlanInvoiceId
  const currency_code = props.school_details.currency_code
  const currency_symbol = props.school_details.currency_symbol
  const courseTier = props.courseTier
  const amount = props.amount
  const productName = props.productName
  const [isCouponCodeValid, setIsCouponCodeValid] = React.useState(false)
  const [automaticApplyCoupon, setApplyCouponAutomatically] = React.useState(
    false,
  )

  React.useEffect(() => {
    if (!props.openDialog) {
      setApplyCouponAutomatically(false)
      return
    }

    if (!(props.courseId || props.paymentPlanId)) return
    const coupon = sessionStorage.getItem(
      props.courseId
        ? `course_coupon_${props.courseId}`
        : `program_coupon_${props.paymentPlanId}`,
    )

    if (coupon) {
      console.log('coupon_exists', coupon)
      setCouponCode(coupon)
      setApplyCouponAutomatically(true)
    }
  }, [props.openDialog])

  React.useEffect(() => {
    if (!automaticApplyCoupon) return
    handleCoupon()
    setApplyCouponAutomatically(false)
  }, [automaticApplyCoupon])

  const [paymentProvider] = React.useState(
    retrieveSchoolPaymentProvider(props.school_details),
  )

  const mySnackBarComponent = useSnackbarComponent()
  const isSmallScreenHook = useIsSmallScreen() // useMediaQuery(theme.breakpoints.down("xs"));
  const { isSmallScreen } = isSmallScreenHook

  const paystackCallback = (response1: any) => {
    console.log(response1)
    if (response1) {
      // setEnableButton(false);
      props.setTransactionComplete(true)
    }

    props.onTransactionComplete && props.onTransactionComplete()
    try {
      props.onTransactionComplete && toggleOpenDialogCoupon()
    } catch (error) {
      console.log(error)
    }
  }
  const paystackOnClose = () => {
    console.log('closed paystack')
  }

  const flutterwaveCallback = (response1: any) => {
    console.log(response1)
    if (response1) {
      // setEnableButton(false);
      props.setTransactionComplete(true)
    }

    props.onTransactionComplete && props.onTransactionComplete()
    try {
      props.onTransactionComplete && toggleOpenDialogCoupon()
    } catch (error) {
      console.log(error)
    }
  }
  const flutterwaveOnClose = () => {
    console.log('closed flutterwave')
  }

  function toggleOpenDialogCoupon() {
    props.setOpenDialog(!props.openDialog)
  }

  function toggleShowCoupon() {
    setShowCoupon(!showCoupon)
  }

  const handleCouponChange = (event: any) => {
    setError('')
    const code = event.target.value.trim()
    setCouponCode(code)
  }

  const handleApply100Discount = async () => {
    try {
      await ApplyCouponRequest({
        opts: {
          couponCode: couponCode,
          courseId: course_id || null,
          schoolId: props.school_details._id,
          studentId: props.userDetails._id,
          categoryId: null,
          paymentAlertId: payments_alert_id || null,
          paymentPlanId: payment_plan_invoice_id || null,
          currency: currency_code,
        },
      })
      setIsCouponCodeValid(true)
      props.setOpenDialog(false)
      mySnackBarComponent.open('Successfully applied 100% discount', 'success')
      props.onTransactionComplete && props.onTransactionComplete()
    } catch (error) {
      console.log(error)
      setIsCouponCodeValid(false)
      mySnackBarComponent.open(
        `
      Unable to apply coupon`,
        'error',
      )
    }
  }

  const cancelCoupon = () => {
    setCouponPrice(0)
    setDiscount(0)
    setIsCouponCodeValid(false)
    setCouponCode('')
  }

  const handleCoupon = async () => {
    setIsCouponCodeValid(false)
    try {
      setApplyCouponLoading(true)
      const coupon = await props.getCoupon(couponCode)
      setApplyCouponLoading(false)
      console.log(coupon)
      if (!coupon.data) {
        setError('Invalid Coupon')
        return
      }

      if (coupon.data.usage_count === coupon.data.allowable_count) {
        setError('Coupon has been used up')
        return
      }

      if (coupon.data.deleted === true) {
        setError('Invalid Coupon')
        return
      }
      if (coupon.data.coupon_type === 'course') {
        if (coupon.data.course_id !== course_id) {
          setError('This coupon is invalid here')
          return
        }
      }
      if (coupon.data.coupon_type === 'cluster') {
        if (coupon.data.cluster_id !== payments_alert_id) {
          setError('This coupon is invalid here')
          return
        }
      }
      if (
        coupon.data.expiry_date &&
        moment(coupon.data.expiry_date).isBefore()
      ) {
        setError('This coupon has expired!')
        return
      }

      const couponDiscount = coupon.data.percentage_off
      const newAmount = amount - amount * (couponDiscount / 100)

      setCouponPrice(Number(newAmount.toFixed(2)))
      setCount(coupon.data.usage_count)
      setDiscount(couponDiscount)
      setIsCouponCodeValid(true)
      setError('')

      mySnackBarComponent.open(
        `Successfully applied a ${couponDiscount}% discount`,
        'success',
      )
      AnalyticService.onCouponAppliedSuccessfully({
        name: name,
        coupon_type: coupon.data.coupon_type,
        'course/cluster': course_id
          ? `course_${course_id}`
          : payments_alert_id
          ? `program_${payments_alert_id}`
          : payment_plan_invoice_id
          ? `program_invoice_${payment_plan_invoice_id}`
          : '',
        studentId: props.userDetails._id,
        percentage_off: couponDiscount,
      })
    } catch (error) {
      mySnackBarComponent.open('Unable to apply coupon', 'error')
      setApplyCouponLoading(false)
    }
  }

  return (
    <div
      style={{
        marginTop: '10px',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <DialogComponent
        title="Make Payment"
        open={props.openDialog}
        fullScreen={!!isSmallScreen}
        content={
          <div>
            <List dense={true}>
              <ListItem>
                <ListItemText style={{ width: '100px', maxWidth: '100px' }}>
                  <b> Amount:</b>
                </ListItemText>
                <ListItemText>
                  {currency_symbol}
                  {formatAmount(amount)}
                </ListItemText>
              </ListItem>
              <ListItem>
                <ListItemText style={{ width: '150px', maxWidth: '100px' }}>
                  <b> Item:</b>
                </ListItemText>
                <ListItemText>{props.productName}</ListItemText>
              </ListItem>
              <ListItem>
                <ListItemText style={{ width: '150px', maxWidth: '100px' }}>
                  <b> Discount: </b>
                </ListItemText>
                <ListItemText>
                  {discount
                    ? `${discount}%  (-${currency_code}${formatAmount(
                        amount - couponPrice,
                      )})`
                    : 0}
                </ListItemText>
                <ListItemText>
                  <div>
                    <Button
                      onClick={toggleShowCoupon}
                      color={'primary'}
                      variant={'text'}
                    >
                      Add coupon code{' '}
                      {!showCoupon ? <ArrowDownIcon /> : <ArrowUpIcon />}
                    </Button>
                  </div>
                </ListItemText>
              </ListItem>
              <ListItem style={{ display: 'block' }}>
                {showCoupon && (
                  <div>
                    <Paper variant={'outlined'} style={{ padding: '10px' }}>
                      <FormControl>
                        <TextField
                          id="outlined-name"
                          label="Enter coupon code"
                          variant="filled"
                          size="small"
                          value={couponCode}
                          helperText={error || 'This is optional'}
                          error={Boolean(error)}
                          onChange={handleCouponChange}
                        />
                      </FormControl>
                      <br />
                      <div>
                        <Button
                          onClick={handleCoupon}
                          color="primary"
                          style={{ marginRight: '2px' }}
                          variant="outlined"
                          size="small"
                          disableElevation={true}
                          disabled={applyCouponLoading}
                        >
                          Apply
                          {applyCouponLoading && <CircularProgress size={15} />}
                        </Button>
                        <Button
                          onClick={cancelCoupon}
                          color="primary"
                          variant="outlined"
                          size="small"
                          disableElevation={true}
                          disabled={!couponPrice && couponPrice != 0}
                        >
                          {' '}
                          Cancel{' '}
                        </Button>
                      </div>
                    </Paper>
                  </div>
                )}
              </ListItem>
              <ListItem>
                <ListItemText style={{ width: '150px', maxWidth: '100px' }}>
                  <b> Total:</b>
                </ListItemText>
                <ListItemText>
                  {currency_symbol}
                  {discount === 100
                    ? 0
                    : formatAmount(couponPrice) || formatAmount(amount)}
                </ListItemText>
              </ListItem>
            </List>

            <div style={{ marginTop: '15px', width: '100%' }}>
              {discount === 100 ? (
                <div style={{ textAlign: 'center' }}>
                  <Button
                    color="primary"
                    variant="contained"
                    size="large"
                    fullWidth={true}
                    onClick={handleApply100Discount}
                  >
                    Continue
                  </Button>
                </div>
              ) : paymentProvider === 'paystack' ? (
                <PayWithPaystack
                  currency={props.school_details.currency_code}
                  metadata={{
                    user_name: name,
                    user_id: user_id,
                    // school_details: this.props.school_details,
                    school_id: school_id,
                    additional_info: additional_info,
                    email: email,
                    phone_number: phone_number,
                    payment_for: payment_for,
                    course_id: course_id,
                    payments_alert_id: payments_alert_id,
                    payment_plan_invoice_id: payment_plan_invoice_id,
                    coupon_code: isCouponCodeValid ? couponCode : undefined,
                    course_tier: courseTier,
                  }}
                  text={'Continue'}
                  embed={false}
                  email={props.userDetails.email}
                  callback={paystackCallback}
                  close={paystackOnClose}
                  paystackkey={PAYSTACK_KEY}
                  amount={couponPrice ? couponPrice * 100 : amount * 100} // paystack is in kobo so multiply by 100
                  disabled={!(amount > 0) || !email || !name}
                />
              ) : paymentProvider === 'flutterwave' ? (
                <PayWithFlutterwave
                  currency={props.school_details.currency_code}
                  metadata={{
                    user_name: name,
                    user_id: user_id,
                    school_id: school_id,
                    additional_info: additional_info,
                    email: email,
                    phone_number: phone_number,
                    payment_for: payment_for,
                    course_id: course_id,
                    payments_alert_id: payments_alert_id,
                    payment_plan_invoice_id: payment_plan_invoice_id,
                    coupon_code: isCouponCodeValid ? couponCode : undefined,
                    course_tier: courseTier,
                  }}
                  text={'Continue'}
                  email={props.userDetails.email}
                  callback={flutterwaveCallback}
                  close={flutterwaveOnClose}
                  amount={couponPrice || amount}
                  disabled={!(amount > 0) || !email || !name}
                  success_url={
                    location.pathname == '/pay'
                      ? location.origin
                      : location.href
                  }
                  logo={
                    props.school_details?.logo ||
                    'https://pledre.com/static/img/logo.png'
                  }
                />
              ) : (
                <PayWithStripe
                  buttonVariant={props.buttonVariant}
                  // success_url={`${location.origin}/payment-successful`}
                  success_url={
                    location.pathname == '/pay'
                      ? location.origin
                      : location.href
                  }
                  cancel_url={`${location.href}`}
                  currency={props.school_details.currency_code}
                  product_name={productName}
                  metadata={{
                    user_name: name,
                    // school_details: this.props.school_details,
                    school_id: props.school_details._id,
                    additional_info: additional_info,
                    email: email,
                    usage: count,
                    code: couponCode,
                    close: toggleOpenDialogCoupon,
                    phone_number: phone_number,
                    payment_for: payment_for,
                    course_id: course_id,
                    payments_alert_id: payments_alert_id,
                    payment_plan_invoice_id: payment_plan_invoice_id,
                    coupon_code: isCouponCodeValid ? couponCode : undefined,
                    course_tier: courseTier,
                  }}
                  text={'Continue'}
                  email={email}
                  amount={couponPrice ? couponPrice * 100 : amount * 100} // paystack is in kobo so multiply by 100
                  disabled={!(amount > 0) || !email || !name}
                />
              )}
            </div>
            <div>
              <HubspotSupportFormDialogButton buttonText="Having problems paying?" />
            </div>
          </div>
        }
        onRequestClose={toggleOpenDialogCoupon}
      />
    </div>
  )
}
